/** Lançar: uma folha por vez, com o impacto antes de confirmar.
 *
 * Reescrita em 12/09, depois do Paulo dizer que os modais estavam "mal feitos, caixas
 * largas, opções de seleção estranhas, categorias mal organizadas". Ele tinha razão em
 * tudo, e um dos itens era defeito: lançando uma SAÍDA, a lista oferecia "Salário",
 * "Freela" e "Rendimentos", porque o filtro abria todas as categorias quando o tipo era
 * saída.
 *
 * Três decisões desta versão:
 *
 * 1. ONDE O DINHEIRO SAI é uma pergunta que muda de sentido conforme a resposta. De uma
 *    conta, sai agora; de um cartão, entra na fatura e sai no vencimento. A folha separa
 *    os dois grupos e diz a data em que o dinheiro vai embora de verdade.
 * 2. CATEGORIA SEGUE O TIPO. Saída vê fixas e variáveis, entrada vê receitas, diário vê
 *    diário, economia vê reserva.
 * 3. O BOTÃO NÃO SOME. O rodapé fica fixo com o impacto ao lado; antes era preciso rolar
 *    a folha inteira para achar "Lançar", e o impacto ficava cortado pela borda.
 */

import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Field, FieldGroup, Key, Label, Switch } from "../components/ui";
import { Zone, zonaDe, zoneClass } from "../components/ui";
import { useToast } from "../components/Toast";
import { addDays, dayMonth, hojeLocal, money, monthRange, parseMoney } from "../lib/format";
import { usePrefs } from "../lib/prefs";
import { useSimulation } from "../lib/simulation";
import {
  useAccounts,
  useCategories,
  useCreateCategory,
  useCreateTransaction,
  useProjection,
  useSettingsQuery,
} from "../lib/queries";
import type { Account, Bucket, TxKind } from "../lib/types";

type Tipo = "in" | "out" | "dy" | "sv";

/** Cada tipo diz que família de categoria faz sentido oferecer. */
const TIPOS: { id: Tipo; label: string; kind: TxKind; bucket: Bucket; categorias: Bucket[] }[] = [
  { id: "in", label: "Entrada", kind: "income", bucket: "income", categorias: ["income"] },
  { id: "out", label: "Saída", kind: "expense", bucket: "fixed", categorias: ["fixed", "variable"] },
  { id: "dy", label: "Diário", kind: "expense", bucket: "daily", categorias: ["daily"] },
  { id: "sv", label: "Economia", kind: "transfer", bucket: "savings", categorias: ["savings"] },
];

/** Vencimento da fatura que recebe uma compra feita nesta data. Quem decide de verdade é
 *  o motor, no servidor; aqui serve para a folha poder dizer quando o dinheiro sai. */
function venceEm(cartao: Account, compra: string): string | null {
  if (!cartao.closing_day || !cartao.due_day) return null;
  const [ano, mes, dia] = compra.split("-").map(Number);
  // >= e não >: o servidor fecha a fatura NO dia do fechamento (api/app/finance.py e a
  // regra em SQL). Com > o app dizia "sai em 20/10" e o motor jogava para 20/11 — errado
  // por um mês inteiro numa compra feita exatamente no dia do fechamento.
  const passouDoFechamento = dia >= cartao.closing_day;
  const desloca = (passouDoFechamento ? 1 : 0) + (cartao.due_day < cartao.closing_day ? 1 : 0);
  const alvo = mes + desloca;
  const a = ano + Math.floor((alvo - 1) / 12);
  const m = ((alvo - 1) % 12) + 1;
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  const d = Math.min(cartao.due_day, ultimo);
  return `${a}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function Lancar() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sim = useSimulation();
  const { scope } = usePrefs();
  const criar = useCreateTransaction();
  const aviso = useToast();

  const hoje = hojeLocal();
  const volta = params.get("volta") ?? "/hoje";
  const [tipo, setTipo] = useState<Tipo>("out");
  const [valor, setValor] = useState("");
  const [desc, setDesc] = useState("");
  const [data, setData] = useState(params.get("dia") ?? hoje);
  const [contaId, setContaId] = useState("");
  const [destinoId, setDestinoId] = useState("");
  const [catId, setCatId] = useState("");
  const [parcelas, setParcelas] = useState(1);
  const [pago, setPago] = useState(false);
  const [soSimular, setSoSimular] = useState(sim.active);
  const [erro, setErro] = useState<string | null>(null);
  const [novaCat, setNovaCat] = useState("");

  const { data: contas } = useAccounts();
  const { data: cats } = useCategories();
  const { data: settings } = useSettingsQuery();
  const criarCat = useCreateCategory();
  const t = TIPOS.find((x) => x.id === tipo)!;

  // Receita e economia não entram em cartão: o motor recusa, e oferecer seria mentira.
  const aceita = useMemo(() => {
    const todas = (contas ?? []).filter((a) => !a.archived_at);
    if (tipo === "in" || tipo === "sv") return todas.filter((a) => a.in_cashflow);
    return todas.filter((a) => a.in_cashflow || a.kind === "credit_card" || a.kind === "loan");
  }, [contas, tipo]);

  const doCaixa = aceita.filter((a) => a.in_cashflow);
  const aPrazo = aceita.filter((a) => a.kind === "credit_card" || a.kind === "loan");
  const reservas = useMemo(
    () => (contas ?? []).filter((a) => a.kind === "savings" || a.kind === "investment"),
    [contas],
  );

  const conta = aceita.find((a) => a.id === contaId) ?? doCaixa[0] ?? aceita[0];
  const ehPrazo = conta?.kind === "credit_card" || conta?.kind === "loan";
  const saiEm = ehPrazo && conta ? venceEm(conta, data) : data;

  const categorias = (cats ?? []).filter((c) => t.categorias.includes(c.bucket as Bucket));

  const amount = parseMoney(valor);
  const valido = amount > 0 && !!conta && (tipo !== "sv" || !!(destinoId || reservas[0]));

  // Impacto: o fim do mês com e sem este lançamento. O servidor é a fonte do saldo.
  const { last } = monthRange(data);
  // `to` nunca antes de `frm`: lançar "ontem" no dia 1º pedia um intervalo invertido e a
  // API devolvia 422, sumindo com o painel de impacto.
  const { data: proj } = useProjection({ scope, frm: hoje, to: last < hoje ? hoje : last });
  const fimAtual = proj?.days.at(-1);
  // Compra no cartão que vence depois deste mês não muda o fim do mês: o caixa não sente.
  const pesaNoMes = !saiEm || saiEm <= last;
  // Transferência entre duas contas do fluxo não muda o caixa: sai de uma e entra na
  // outra. Mostrar "−valor" aqui seria assustar à toa.
  const destino = (contas ?? []).find((a) => a.id === (destinoId || reservas[0]?.id));
  const ficaNoCaixa = t.kind === "transfer" && !!destino?.in_cashflow;
  const efeito = ficaNoCaixa ? 0 : t.kind === "income" ? amount || 0 : -(amount || 0);
  const fimDepois = fimAtual && pesaNoMes ? fimAtual.end + efeito : (fimAtual?.end ?? null);

  const simulando = soSimular || sim.active;

  const enviar = async () => {
    if (!valido || !conta) return;
    setErro(null);
    try {
      const scenario_id = simulando ? await sim.ensure() : null;
      const nome = desc || categorias.find((c) => c.id === catId)?.name || t.label;
      await criar.mutateAsync({
        account_id: conta.id,
        kind: t.kind,
        amount,
        occurred_on: data,
        counter_account_id: tipo === "sv" ? destinoId || reservas[0]?.id : null,
        category_id: catId || null,
        description: nome,
        status: pago ? "cleared" : "pending",
        scenario_id,
        installments: parcelas > 1 ? parcelas : null,
      });
      if (!scenario_id) {
        aviso.ok(
          ehPrazo && saiEm
            ? `${nome} · na fatura que vence ${dayMonth(saiEm)}`
            : `${nome} · ${money(amount)}`,
        );
      }
      nav(volta);
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-labelledby="lancar-titulo">
      <div className="panel lancar">
        <div className="handle" />
        <h2 id="lancar-titulo">
          <span>
            Lançar{" "}
            {simulando && (
              <span className="tag" style={{ color: "var(--sim-t)", borderColor: "var(--sim)" }}>
                simulado
              </span>
            )}
          </span>
          <Key icon="x" title="Fechar" onClick={() => nav(volta)} />
        </h2>

        <div className="corpo">
          <div className="kinds" role="group" aria-label="Tipo">
            {TIPOS.map((x) => (
              <button
                key={x.id}
                type="button"
                className={`key ${x.id}`}
                aria-pressed={tipo === x.id}
                onClick={() => {
                  setTipo(x.id);
                  setCatId("");
                  setParcelas(1);
                  setContaId("");
                }}
              >
                {x.label}
              </button>
            ))}
          </div>

          {/* valor e descrição dividem a linha: a descrição não merece uma caixa da
              largura da tela só para ela */}
          <div className="duo">
            <Field label="Valor" className={`amount p-${tipo}`}>
              <input
                inputMode="decimal"
                placeholder="0,00"
                value={valor}
                autoFocus
                onChange={(e) => setValor(e.target.value)}
              />
              {/* o valor entendido, escrito por extenso: quem digita "1234.56" precisa ver
                  na hora que o app leu R$ 1.234,56, e não R$ 123.456,00 */}
              {valor.trim() !== "" && <span className="eco">{money(amount)}</span>}
            </Field>
            <Field label="Descrição">
              <input
                placeholder={tipo === "in" ? "De onde veio" : "No que foi"}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
              />
            </Field>
          </div>

          <FieldGroup label={tipo === "in" ? "Cai em" : "Sai de"}>
            <div className="chips">
              {doCaixa.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="chip"
                  aria-pressed={conta?.id === a.id}
                  onClick={() => setContaId(a.id)}
                >
                  <i style={{ background: a.color ?? "var(--muted)" }} />
                  {a.name}
                </button>
              ))}
            </div>
          </FieldGroup>

          {aPrazo.length > 0 && (
            <FieldGroup label="Ou no crédito">
              <div className="chips">
                {aPrazo.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="chip credito"
                    aria-pressed={conta?.id === a.id}
                    onClick={() => setContaId(a.id)}
                  >
                    <i style={{ background: a.color ?? "var(--muted)" }} />
                    {a.name}
                  </button>
                ))}
              </div>
            </FieldGroup>
          )}

          {ehPrazo && saiEm && (
            <div className="aviso-prazo">
              Não sai do caixa agora: entra na fatura e o dinheiro sai em <b>{dayMonth(saiEm)}</b>.
            </div>
          )}

          {/* Sem reserva, Economia é um botão morto: o campo "Vai para" some, o botão
              Lançar fica desabilitado para sempre e nada explica. O app já sabe dizer isso
              direito no Planejar quando falta cartão — aqui não dizia. */}
          {tipo === "sv" && reservas.length === 0 && (
            <div className="banner" role="status">
              Economia é dinheiro que sai do fluxo e vai para uma reserva, e você ainda não
              tem nenhuma. Crie em <b>Contas → Nova reserva</b> e volte aqui.
            </div>
          )}

          {tipo === "sv" && reservas.length > 0 && (
            <FieldGroup label="Vai para">
              <div className="chips">
                {reservas.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    className="chip"
                    aria-pressed={(destinoId || reservas[0].id) === a.id}
                    onClick={() => setDestinoId(a.id)}
                  >
                    <i style={{ background: a.color ?? "var(--muted)" }} />
                    {a.name}
                  </button>
                ))}
              </div>
            </FieldGroup>
          )}

          <FieldGroup label={ehPrazo ? "Compra em" : "Quando"}>
            <div className="row2">
              <div className="chips">
                <button
                  type="button"
                  className="chip"
                  aria-pressed={data === hoje}
                  onClick={() => setData(hoje)}
                >
                  Hoje
                </button>
                <button
                  type="button"
                  className="chip"
                  aria-pressed={data === addDays(hoje, -1)}
                  onClick={() => setData(addDays(hoje, -1))}
                >
                  Ontem
                </button>
              </div>
              <input type="date" value={data} onChange={(e) => setData(e.target.value)} aria-label="Data" />
            </div>
          </FieldGroup>

          {ehPrazo && tipo !== "dy" && (
            <FieldGroup label="Parcelas">
              <div className="chips">
                {[1, 2, 3, 4, 6, 10, 12].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className="chip"
                    aria-pressed={parcelas === n}
                    onClick={() => setParcelas(n)}
                  >
                    {n === 1 ? "à vista" : `${n}×`}
                  </button>
                ))}
              </div>
              {parcelas > 1 && amount > 0 && (
                <div className="rule">
                  {parcelas}× de {money(amount / parcelas)}, uma por fatura
                </div>
              )}
            </FieldGroup>
          )}

          <FieldGroup label="Categoria">
            <div className="chips">
              {categorias.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="chip"
                  aria-pressed={catId === c.id}
                  onClick={() => setCatId(catId === c.id ? "" : c.id)}
                >
                  {c.name}
                </button>
              ))}
              {/* a categoria que falta nasce aqui, no momento em que se precisa dela —
                  era assim na v2, e mandar a pessoa para Ajustes no meio de um
                  lançamento é perder o lançamento */}
              <input
                className="chip nova"
                placeholder="+ nova"
                value={novaCat}
                aria-label="Criar categoria"
                onChange={(e) => setNovaCat(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key !== "Enter" || !novaCat.trim()) return;
                  e.preventDefault();
                  try {
                    const c = await criarCat.mutateAsync({
                      name: novaCat.trim(),
                      bucket: t.categorias[0],
                    });
                    setCatId(c.id);
                    setNovaCat("");
                    aviso.ok(`Categoria ${c.name} criada`);
                  } catch (err) {
                    aviso.erro((err as Error).message);
                  }
                }}
              />
            </div>
          </FieldGroup>

          <div className="switches">
            <Switch checked={pago} onChange={setPago}>
              {tipo === "in" ? "Já recebi" : ehPrazo ? "Já está na fatura" : "Já paguei"}
            </Switch>
            <Switch checked={simulando} onChange={setSoSimular} tone="sim">
              Só simular
            </Switch>
          </div>

          {erro && (
            <div className="banner" role="alert">
              {erro}
            </div>
          )}
        </div>

        {/* rodapé fixo: confirmar não pode depender de rolagem */}
        <div className="rodape">
          <div className="impacto">
            {fimAtual && amount > 0 ? (
              <>
                <Label>Fim do mês</Label>
                <div className="v">
                  <span className="de">{money(fimAtual.end)}</span>
                  <span className="seta">→</span>
                  {/* a cor é a do RESULTADO, não a do saldo de agora. Antes um "depois"
                      negativo saía pintado de verde: a cor dizendo "tudo bem" em cima de
                      um número que diz o contrário. */}
                  <span className={`t-${zonaDe(fimDepois ?? 0, settings?.settings.health_levels)}`}>
                    {money(fimDepois ?? 0)}
                  </span>
                </div>
                {!pesaNoMes && <span className="rule">a fatura vence no mês seguinte</span>}
              </>
            ) : (
              fimAtual && (
                <>
                  <Label>Fim do mês</Label>
                  <div className="v">
                    <span className={`t-${zoneClass(fimAtual.color)}`}>{money(fimAtual.end)}</span>
                    <Zone color={fimAtual.color} />
                  </div>
                </>
              )
            )}
          </div>
          <div className="acts">
            <Key onClick={() => nav(volta)}>Cancelar</Key>
            <Key primary disabled={!valido || criar.isPending} onClick={enviar}>
              {criar.isPending ? "Lançando…" : simulando ? "Simular" : "Lançar"}
            </Key>
          </div>
        </div>
      </div>
    </div>
  );
}
